/**
 * Owns: Horizon product form (submit, variant morph, quantity, cart errors).
 * Exposes: ProductFormComponent, AddToCartComponent.
 * Consumes: StorePdpFormAdapter on store-inventory PDPs (delegates store sync/validation).
 */

import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { cartQueue } from '@theme/cart-queue';
import { morph } from '@theme/morph';
import { asStoreProductAvailability } from '@theme/store-product-availability';
import { STORE_AVAILABILITY_LOADING_EVENT, STORE_INVENTORY_SETTLED_EVENT } from '@theme/store-events';
import { getStoreProductAvailability, isStoreInventoryProductPage, readContinueSellingSignalsFromElement, validateProductFormAddToCart } from '@theme/store-inventory';
import { StorePdpFormAdapter } from '@theme/store-pdp-form-adapter';
import { getSelectedStoreCode } from '@theme/store-context';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} buyButtonsPending - Loading overlay on buy buttons during settlement.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
export class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #variantFetchPending = false;

  /** @type {boolean} */
  #storeInventoryPending = false;

  /** Used by {@link StorePdpFormAdapter} and store-availability loading events. */
  get storeInventoryPending() {
    return this.#storeInventoryPending;
  }

  set storeInventoryPending(value) {
    this.#storeInventoryPending = value;
  }

  /** @type {boolean} */
  #addToCartInProgress = false;

  /** @type {StorePdpFormAdapter | null} */
  #storeAdapter = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener('variant-picker:loading', this.#onVariantPickerLoading, { signal });
    target?.addEventListener(STORE_AVAILABILITY_LOADING_EVENT, this.#onStoreAvailabilityLoading, { signal });
    target?.addEventListener(STORE_INVENTORY_SETTLED_EVENT, this.#onStoreInventorySettled, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const productId = this.dataset.productId?.trim();
    if (productId && isStoreInventoryProductPage(productId)) {
      this.#storeAdapter = new StorePdpFormAdapter(this);
      this.#storeAdapter.connect(signal);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    this.#storeAdapter?.sync();

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    void this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * @param {FormData} formData
   */
  async #appendStoreCodeProperty(formData) {
    const storeCode = await getSelectedStoreCode();
    if (storeCode) {
      formData.set('properties[_store_code]', storeCode);
    }
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** Used by {@link StorePdpFormAdapter}. */
  getQuantity() {
    return this.#getQuantity();
  }

  /** Used by {@link StorePdpFormAdapter}. */
  isBuyButtonsSettlementPending() {
    return this.#isBuyButtonsSettlementPending();
  }

  /**
   * Used by {@link StorePdpFormAdapter}.
   * @param {string} message
   * @param {NodeListOf<Element>} allAddToCartContainers
   */
  showAddToCartQuantityError(message, allAddToCartContainers) {
    this.#showAddToCartQuantityError(message, allAddToCartContainers);
  }

  /**
   * Used by {@link StorePdpFormAdapter}.
   * @returns {NodeListOf<Element>}
   */
  getAddToCartContainers() {
    return this.querySelectorAll('add-to-cart-component');
  }

  /**
   * @returns {boolean}
   */
  #hasCollectionStoreInventoryValidation() {
    const continueSignals = readContinueSellingSignalsFromElement(this);
    return (
      (this.dataset.storeLocatorAvailabilities?.trim() ?? '').length > 0 ||
      this.dataset.storeInventoryEnabled === 'true' ||
      continueSignals.inventoryPolicy === 'continue' ||
      continueSignals.inStockInStoreValues.length > 0
    );
  }

  /**
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers
   * @param {number} [overrideQuantity]
   * @param {string} [overrideVariantId]
   * @returns {Promise<boolean>}
   */
  async #validateStoreInventoryBeforeAdd(allAddToCartContainers, overrideQuantity, overrideVariantId) {
    const variantId = overrideVariantId?.trim() ?? this.refs.variantId?.value?.trim();
    if (!variantId) return true;

    const quantityToAdd = overrideQuantity !== undefined ? overrideQuantity : this.#getQuantity();

    const validation = await validateProductFormAddToCart({
      formElement: this,
      variantId,
      requestedQuantity: quantityToAdd,
      cartQuantity: 0,
      storeLocatorAvailabilities: this.dataset.storeLocatorAvailabilities,
    });

    if (validation.ok) return true;

    const message =
      validation.message ??
      Theme.translations?.store_inventory_cart_not_available ??
      "This item isn't available at your selected store.";
    this.#showAddToCartQuantityError(message, allAddToCartContainers);
    return false;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    const useSubmitLock = !overrideVariantId;

    if (useSubmitLock) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;

      if (!this.#beginAddToCartSubmit(allAddToCartContainers)) return;
    }

    try {
      if (useSubmitLock && this.#storeAdapter?.isActive() && !this.#storeAdapter.canAddForFulfillment()) {
        return;
      }

      const form = this.querySelector('form');
      if (!form) throw new Error('Product form element missing');

      if (useSubmitLock && this.refs.quantitySelector?.canAddToCart) {
        const validation = this.refs.quantitySelector.canAddToCart();

        if (!validation.canAdd) {
          const errorTemplate = this.dataset.quantityErrorMax || '';
          const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
          this.#showAddToCartQuantityError(errorMessage, allAddToCartContainers);
          return;
        }
      }

      if (
        useSubmitLock &&
        this.#storeAdapter?.isActive() &&
        !(await this.#storeAdapter.validateAddToCart(allAddToCartContainers, overrideQuantity))
      ) {
        return;
      }

      if (
        useSubmitLock &&
        !this.#storeAdapter?.isActive() &&
        this.#hasCollectionStoreInventoryValidation() &&
        !(await this.#validateStoreInventoryBeforeAdd(
          allAddToCartContainers,
          overrideQuantity,
          overrideVariantId
        ))
      ) {
        return;
      }

      const formData = new FormData(form);

      if (overrideVariantId) {
        formData.set('id', overrideVariantId);
      }
      if (overrideQuantity !== undefined) {
        formData.set('quantity', overrideQuantity.toString());
      }

      await this.#appendStoreCodeProperty(formData);

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      formData.append('sections', cartItemComponentsSectionIds.join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const parsedResponse = await cartQueue.enqueue(async () => {
        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        });
        return response.json();
      });

      if (parsedResponse.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', parsedResponse.message, parsedResponse.description, parsedResponse.errors)
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = parsedResponse.message;
          } else {
            const newTextNode = document.createTextNode(parsedResponse.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(parsedResponse.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );

        return;
      }

      const id = formData.get('id');

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (!id) throw new Error('Form ID is required');

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      const cart = await this.#fetchAndUpdateCartQuantity();

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, id.toString(), {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          productId: this.dataset.productId,
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      if (useSubmitLock) {
        this.#endAddToCartSubmit(allAddToCartContainers);
      }

      if (event) {
        cartPerformance.measureFromEvent('add:user-action', event);
      }
    }
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  async #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!this.#beginAddToCartSubmit(allAddToCartContainers)) return;

    try {
      if (this.#storeAdapter?.isActive()) {
        /** @type {Map<string, number>} */
        const addByVariant = new Map();
        for (const item of items) {
          addByVariant.set(item.variantId, (addByVariant.get(item.variantId) ?? 0) + item.quantity);
        }

        /** @type {Array<{ variant_id: number; quantity: number }>} */
        let cartItems = [];
        try {
          const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const cart = await response.json();
            cartItems = Array.isArray(cart?.items) ? cart.items : [];
          }
        } catch {
          /* use empty cart */
        }

        for (const [variantId, quantityToAdd] of addByVariant) {
          const cartQuantity = cartItems
            .filter((line) => String(line.variant_id) === String(variantId))
            .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);

          const ok = await this.#storeAdapter?.validateAddToCart(allAddToCartContainers, quantityToAdd, {
            variantId,
            cartQuantity,
          });
          if (!ok) return;
        }
      }

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      }

      const storeCode = await getSelectedStoreCode();

      const payload = {
        items: items.map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
          properties: storeCode ? { _store_code: storeCode } : {},
        })),
        sections: cartItemComponentsSectionIds.join(','),
      };

      const parsedResponse = await cartQueue.enqueue(async () => {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        return response.json();
      });

      if (parsedResponse.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, parsedResponse.message, parsedResponse.description, parsedResponse.errors)
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = parsedResponse.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(parsedResponse.message));
          }
          this.#setLiveRegionText(parsedResponse.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            productId: this.dataset.productId,
          })
        );
        return;
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      const cart = await this.#fetchAndUpdateCartQuantity();

      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'product-form-component',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#endAddToCartSubmit(allAddToCartContainers);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Re-sync ATC presentation from store inventory after variant DOM updates.
   * Store-inventory PDPs must not rely on morphed Liquid buy-button state.
   */
  async #finalizeStoreInventoryPresentation() {
    if (!this.#storeAdapter?.isActive()) return;

    const availability = asStoreProductAvailability(
      getStoreProductAvailability(this.dataset.productId ?? '')
    );

    const variantId = this.refs.variantId?.value?.trim();
    if (variantId && availability) {
      availability.dataset.currentVariantId = variantId;
    }

    if (availability?.refreshInventory) {
      await availability.refreshInventory();
    }

    await this.#fetchAndUpdateCartQuantity();

    this.#syncStoreInventoryWhenSettled();
  }

  #syncStoreInventoryWhenSettled() {
    if (!this.#storeAdapter?.isActive()) return;

    const attempt = () => {
      if (!this.#storeAdapter?.isActive()) return;

      // Store inventory gate + row are authoritative once settled; do not wait on variant section fetch.
      if (!this.#storeInventoryPending) {
        this.#storeAdapter.sync();
        return;
      }

      requestAnimationFrame(attempt);
    };

    requestAnimationFrame(attempt);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    const isStoreInventory = Boolean(this.#storeAdapter?.isActive());

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    if (isStoreInventory) {
      const nextVariantId = event.detail.resource?.id;
      const availability = asStoreProductAvailability(
        getStoreProductAvailability(this.dataset.productId ?? '')
      );
      if (nextVariantId && availability) {
        availability.dataset.currentVariantId = String(nextVariantId);
      }
    }

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      void this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
      this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
      return;
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (!isStoreInventory && newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (!isStoreInventory) {
      this.#applyVariantAvailabilityToAddToCart(event.detail.resource, {
        addToCartButtonContainer: currentAddToCartButtonContainer,
        acceleratedCheckoutButtonContainer,
      });
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      const liveQuantityInput = /** @type {{ refs: { quantityInput?: HTMLInputElement } }} */ (quantitySelector).refs
        .quantityInput;
      if (liveQuantityInput instanceof HTMLInputElement) {
        if (newQuantityInput.max) {
          liveQuantityInput.dataset.shopifyQuantityMax = newQuantityInput.max;
        } else {
          delete liveQuantityInput.dataset.shopifyQuantityMax;
        }
        liveQuantityInput.removeAttribute('data-store-quantity-max-applied');
      }
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (!isStoreInventory && productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        if (this.#isBuyButtonsSettlementPending()) {
          this.#setBuyButtonsDisabledForVariantFetch(true);
        }
      } else {
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
        }
      }

      if (productFormButtons && newProductFormButtons) {
        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          const quantityInput = /** @type {{ refs: { quantityInput?: HTMLInputElement } }} */ (
            this.refs.quantitySelector
          ).refs.quantityInput;
          if (quantityInput instanceof HTMLInputElement) {
            if (newQuantityInputElement.max) {
              quantityInput.dataset.shopifyQuantityMax = newQuantityInputElement.max;
            } else {
              delete quantityInput.dataset.shopifyQuantityMax;
            }
            quantityInput.removeAttribute('data-store-quantity-max-applied');
          }
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (isStoreInventory) {
      await this.#finalizeStoreInventoryPresentation();
      return;
    }

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };

  /** @param {Event} event */
  #onVariantPickerLoading = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const detail = /** @type {{ loading?: boolean; productId?: string }} */ (event.detail ?? {});
    if (String(detail.productId ?? '') !== String(this.dataset.productId ?? '')) return;

    this.#variantFetchPending = Boolean(detail.loading);
    this.#updateBuyButtonsSettlementPending();

    if (detail.loading) {
      return;
    }

    if (this.#storeAdapter?.isActive()) {
      this.#syncStoreInventoryWhenSettled();
      return;
    }

    if (this.#variantChangeInProgress) {
      this.#variantChangeInProgress = false;
      if (!this.#isBuyButtonsSettlementPending()) {
        this.#restoreBuyButtonsAfterVariantFetch();
      }
    }
  };

  /** @param {Event} event */
  #onStoreAvailabilityLoading = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const detail = /** @type {{ loading?: boolean; productId?: string }} */ (event.detail ?? {});
    if (String(detail.productId ?? '') !== String(this.dataset.productId ?? '')) return;

    this.#storeInventoryPending = Boolean(detail.loading);
    this.#updateBuyButtonsSettlementPending();
  };

  /** @param {Event} event */
  #onStoreInventorySettled = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const detail = /** @type {{ productId?: string }} */ (event.detail ?? {});
    if (String(detail.productId ?? '') !== String(this.dataset.productId ?? '')) return;
    if (!this.#storeAdapter?.isActive()) return;

    this.#syncStoreInventoryWhenSettled();
  };

  /**
   * @returns {boolean}
   */
  #isBuyButtonsSettlementPending() {
    return (
      this.#variantFetchPending || (Boolean(this.#storeAdapter?.isActive()) && this.#storeInventoryPending)
    );
  }

  #updateBuyButtonsSettlementPending() {
    const pending = this.#isBuyButtonsSettlementPending();
    const buyButtonsBlock = this.closest('.buy-buttons-block');
    const { buyButtonsPending } = this.refs;

    if (buyButtonsBlock instanceof HTMLElement) {
      buyButtonsBlock.classList.toggle('buy-buttons-block--pending', pending);
      buyButtonsBlock.setAttribute('aria-busy', pending ? 'true' : 'false');
    }

    if (buyButtonsPending instanceof HTMLElement) {
      buyButtonsPending.hidden = !pending;
      if (pending) {
        buyButtonsPending.removeAttribute('aria-hidden');
      } else {
        buyButtonsPending.setAttribute('aria-hidden', 'true');
      }
    }

    if (pending) {
      if (this.#storeAdapter?.isActive()) {
        // Only force sold-out while store inventory is loading; variant section fetch must not override a settled gate.
        if (this.#storeInventoryPending) {
          this.#setBuyButtonsDisabledForVariantFetch(true);
        }

        const quantitySelector = this.refs.quantitySelector;
        if (quantitySelector && 'setInteractionEnabled' in quantitySelector) {
          /** @type {{ setInteractionEnabled: (enabled: boolean) => void }} */ (quantitySelector).setInteractionEnabled(
            false
          );
        }
      } else {
        this.#setBuyButtonsDisabledForVariantFetch(true);
      }
    } else if (this.#storeAdapter?.isActive()) {
      this.#storeAdapter.sync();
    }
  }

  /**
   * @param {boolean} disabled
   */
  #setBuyButtonsDisabledForVariantFetch(disabled) {
    if (!disabled) return;

    if (this.#storeAdapter?.isActive()) {
      const availability = asStoreProductAvailability(
        getStoreProductAvailability(this.dataset.productId ?? '')
      );
      availability?.syncAddToCartPresentation(false);
      return;
    }

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const { acceleratedCheckoutButtonContainer } = this.refs;

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    if (acceleratedCheckoutButtonContainer instanceof HTMLElement) {
      acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
    }
  }

  #restoreBuyButtonsAfterVariantFetch() {
    if (this.#storeAdapter?.isActive()) {
      this.#storeAdapter.sync();
      return;
    }

    const productId = this.dataset.productId?.trim();
    if (!productId) return;

    const picker = document.querySelector(
      `variant-picker[data-product-id="${CSS.escape(productId)}"][data-template-product-match="true"]`
    );
    const script = picker?.querySelector('script[type="application/json"]');
    if (!script?.textContent) return;

    try {
      const resource = JSON.parse(script.textContent);
      this.#applyVariantAvailabilityToAddToCart(resource, {
        addToCartButtonContainer: this.refs.addToCartButtonContainer,
        acceleratedCheckoutButtonContainer: this.refs.acceleratedCheckoutButtonContainer,
      });
    } catch {
      /* ignore malformed variant JSON */
    }
  }

  /**
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers
   * @returns {boolean}
   */
  #beginAddToCartSubmit(allAddToCartContainers) {
    if (this.#addToCartInProgress) return false;

    this.#addToCartInProgress = true;

    for (const container of allAddToCartContainers) {
      container.disable();
    }

    return true;
  }

  /**
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers
   */
  #endAddToCartSubmit(allAddToCartContainers) {
    this.#addToCartInProgress = false;

    if (this.#storeAdapter?.isActive()) {
      this.#storeAdapter.sync();
      return;
    }

    for (const container of allAddToCartContainers) {
      container.enable();
    }
  }

  /**
   * @param {string} message
   * @param {NodeListOf<Element>} allAddToCartContainers
   */
  #showAddToCartQuantityError(message, allAddToCartContainers) {
    const { addToCartTextError } = this.refs;

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');

      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        const newTextNode = document.createTextNode(message);
        addToCartTextError.appendChild(newTextNode);
      }

      this.#setLiveRegionText(message);

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }
  }

  /**
   * @param {import('./events').VariantUpdateEvent['detail']['resource']} resource
   * @param {Object} refs
   * @param {AddToCartComponent | undefined} refs.addToCartButtonContainer
   * @param {HTMLElement | undefined} refs.acceleratedCheckoutButtonContainer
   */
  #applyVariantAvailabilityToAddToCart(resource, refs) {
    // Store-inventory PDP: store-product-availability sets ATC after refreshInventory completes.
    if (this.#storeAdapter?.isActive()) {
      return;
    }

    const { addToCartButtonContainer, acceleratedCheckoutButtonContainer } = refs;
    if (!addToCartButtonContainer) return;

    if (resource == null || resource.available === false) {
      addToCartButtonContainer.disable();
    } else {
      addToCartButtonContainer.enable();
    }

    if (acceleratedCheckoutButtonContainer) {
      if (resource == null || resource.available === false) {
        acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer.removeAttribute('hidden');
      }
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
